import { Axios } from "services/axios";

export const industryApi = {
  fetchAllIndustries: async (options = {}) => {
    try {
      const {
        page = 1,
        limit = 10,
        includeFeatures = false,
        activeOnly = false,
      } = options;
      
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        includeFeatures: String(includeFeatures),
        activeOnly: String(activeOnly),
      });

      const response = await Axios.fetch(`/industries?${params}`);
      
      if (!response || !response.data) {
        throw new Error('Invalid response structure from server');
      }

      const responseData = response.data.data || response.data || {};
      
      if (!responseData.industries || !Array.isArray(responseData.industries)) {
        console.warn('Industries data is not an array:', responseData);
        throw new Error('Industries data is not in expected format');
      }

      return responseData;
      
    } catch (error) {
      console.error('Error fetching industries:', error);
      
      let errorMessage = 'Failed to fetch industries';
      
      if (error.response) {
        errorMessage = error.response.data?.message || 
                     `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }
      
      throw new Error(errorMessage);
    }
  },

  createIndustry: async (industryData) => {
    try {
      const formData = new FormData();

      // Append basic fields
      formData.append('name', industryData.name);
      formData.append('description', industryData.description || '');
      formData.append('order', industryData.order || 0);
      formData.append('is_active', industryData.is_active ? 'true' : 'false');

      // Append icon file if available
      if (industryData.iconFile) {
        formData.append('icon', industryData.iconFile);
      }

      // Append color
      if (industryData.color) {
        formData.append('color', industryData.color);
      }

      const response = await Axios.post('/industries', formData, {
        'Content-Type': 'multipart/form-data',
      });

      return response.data.data || response.data;
      
    } catch (error) {
      console.error('Error creating industry:', error);
      
      let errorMessage = 'Failed to create industry';
      
      if (error.response) {
        errorMessage = error.response.data?.message || 
                     `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }
      
      throw new Error(errorMessage);
    }
  },

  fetchIndustryById: async (id) => {
    try {
      if (!id) {
        throw new Error('Industry ID is required');
      }
      
      const response = await Axios.fetch(`/industries/${id}`);
      
      if (!response || !response.data) {
        throw new Error('Invalid response structure from server');
      }

      const industry = response.data.data || response.data;
      
      if (!industry) {
        throw new Error('Industry not found');
      }

      return industry;
      
    } catch (error) {
      console.error('Error fetching industry:', error);
      
      let errorMessage = 'Failed to fetch industry';
      
      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = 'Industry not found';
        } else {
          errorMessage = error.response.data?.message || 
                        `Server error: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }
      
      throw new Error(errorMessage);
    }
  },

  updateIndustry: async (id, industryData) => {
    try {
      if (!id) {
        throw new Error('Industry ID is required for update');
      }

      const formData = new FormData();

      // Append basic fields
      formData.append('name', industryData.name);
      formData.append('description', industryData.description || '');
      formData.append('order', industryData.order || 0);
      formData.append('is_active', industryData.is_active ? 'true' : 'false');

      // Handle icon file
      if (industryData.iconFile instanceof File) {
        formData.append('icon', industryData.iconFile);
      } else if (industryData.icon) {
        formData.append('iconUrl', industryData.icon);
      }

      // Append color
      if (industryData.color) {
        formData.append('color', industryData.color);
      }

      const response = await Axios.put(`/industries/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data.data || response.data;
      
    } catch (error) {
      console.error('Error updating industry:', error);
      
      let errorMessage = 'Failed to update industry';
      
      if (error.response) {
        errorMessage = error.response.data?.message || 
                     `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }
      
      throw new Error(errorMessage);
    }
  },

  deleteIndustry: async (id) => {
    try {
      if (!id) {
        throw new Error('Industry ID is required');
      }

      const response = await Axios.delete(`/industries/${id}`);
      
      return response.data;
      
    } catch (error) {
      console.error('Error deleting industry:', error);
      
      let errorMessage = 'Failed to delete industry';
      
      if (error.response) {
        errorMessage = error.response.data?.message || 
                     `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to reach server';
      } else {
        errorMessage = error.message || errorMessage;
      }
      
      throw new Error(errorMessage);
    }
  },

  // Industry Features API
  createIndustryFeature: async (featureData) => {
    try {
      const response = await Axios.post('/industry-features', featureData);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error creating industry feature:', error);
      throw new Error(error.response?.data?.message || 'Failed to create industry feature');
    }
  },

  updateIndustryFeature: async (id, featureData) => {
    try {
      const response = await Axios.put(`/industry-features/${id}`, featureData);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error updating industry feature:', error);
      throw new Error(error.response?.data?.message || 'Failed to update industry feature');
    }
  },

  deleteIndustryFeature: async (id) => {
    try {
      const response = await Axios.delete(`/industry-features/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting industry feature:', error);
      throw new Error(error.response?.data?.message || 'Failed to delete industry feature');
    }
  },

  // Feature Options API
  createFeatureOption: async (optionData) => {
    try {
      const response = await Axios.post('/feature-options', optionData);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error creating feature option:', error);
      throw new Error(error.response?.data?.message || 'Failed to create feature option');
    }
  },

  updateFeatureOption: async (id, optionData) => {
    try {
      const response = await Axios.put(`/feature-options/${id}`, optionData);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error updating feature option:', error);
      throw new Error(error.response?.data?.message || 'Failed to update feature option');
    }
  },

  deleteFeatureOption: async (id) => {
    try {
      const response = await Axios.delete(`/feature-options/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting feature option:', error);
      throw new Error(error.response?.data?.message || 'Failed to delete feature option');
    }
  },
};
